// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {actionsRolesAllow} from './ActionsRolesAllow';
import type {RoleDescriptionProps} from './RoleDescription';

const supportSpecialistRoleMessages = defineMessages({
  roleName: {
    id:
      'docsApp.components.contrib.roleDescriptions.supportSpecialist.roleName',
    description: 'The name of the support specialist role.',
    defaultMessage: 'Support Specialist',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.supportSpecialist.shortDescription',
    description: 'The short description of the support specialist role.',
    defaultMessage:
      'Can refund payments, resolve disputes, and edit connected accounts',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.supportSpecialist.descriptionOfWhatRoleCanDo',
    description: 'The description of what the support specialist role can do.',
    defaultMessage:
      'This role is for people who need to refund payments, resolve disputes, and edit a connected account’s payout schedule.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.supportSpecialist.descriptionOfWhatRoleCannotDo',
    description:
      "The description of what the support specialist role can't do.",
    defaultMessage:
      "They can't create connected accounts, transfer funds, payout money, or edit any account and product settings.",
  },
});

const supportSpecialistRoleDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...supportSpecialistRoleMessages.roleName} />,
  roleId: 'support_specialist',
  isBeta: false,
  shortDescription: (
    <FormattedMessage {...supportSpecialistRoleMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage
      {...supportSpecialistRoleMessages.descriptionOfWhatRoleCanDo}
    />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage
      {...supportSpecialistRoleMessages.descriptionOfWhatRoleCannotDo}
    />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputes} />,
    <FormattedMessage {...actionsRolesAllow.createViewRefundPayments} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteProducts} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteCustomers} />,
    <FormattedMessage {...actionsRolesAllow.viewOrRejectConnectedAccounts} />,
    <FormattedMessage {...actionsRolesAllow.editConnectedAcctCapabilities} />,
    <FormattedMessage
      {...actionsRolesAllow.editConnectedAccountPayoutSchedule}
    />,
    <FormattedMessage {...actionsRolesAllow.viewRadar} />,
    <FormattedMessage
      {...actionsRolesAllow.addPaymentFingerprintToRadarAllowLists}
    />,
    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.createConnectedAccounts} />,
    <FormattedMessage {...actionsRolesAllow.viewBalance} />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage {...actionsRolesAllow.configureAnyProductSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
  ],
};

export const allSupportRoleProps = [supportSpecialistRoleDescriptionProps];
